/**
 * ParcoursNav — navigation inter-étapes du parcours rénovation Particulier.
 *
 * Pattern : grille de 4 cards (les 4 étapes : bilan, audit, rénovation
 * globale, MAR). L'étape courante est mise en avant (border-brand, halo
 * gradient brand, badge "Vous êtes ici" avec pulse).
 *
 * Utilisé sur les pages stub parcours et hub aides/travaux pour relier
 * les pages entre elles.
 */

import { ArrowRight } from 'lucide-react';
import { Container } from '../../layout/Container';
import { Reveal } from '../../motion/Reveal';
import { getIcon } from '../../../lib/lucide-icons';
import { cn } from '../../../lib/cn';
import { PARCOURS_PARTICULIERS } from '../../../config/parcours-particuliers';
import { PARTICULIER_SECTION } from '../../../config/audience/particulier.ui';
import { ParticulierSectionHeader } from './SectionHeader';

const PARCOURS_ORDER = [
  'bilan-energetique',
  'audit-energetique',
  'renovation-globale',
  'mon-accompagnateur-renov',
] as const;

export interface ParticulierParcoursNavProps {
  /** Slug de l'étape courante (sera mise en avant) */
  currentSlug?: string;
  /** Eyebrow personnalisée */
  eyebrow?: string;
  /** Titre personnalisé */
  title?: string;
  /** Description personnalisée */
  description?: string;
  /** Variante "rich" : avec halo sticky / "compact" : juste 4 cards */
  variant?: 'rich' | 'compact';
  /** Surcharger les classes de section */
  sectionClass?: string;
}

export function ParticulierParcoursNav({
  currentSlug,
  eyebrow = 'Parcours rénovation',
  title = 'Le parcours complet en 4 étapes',
  description = "Du premier bilan jusqu'au versement des aides après travaux.",
  variant = 'rich',
  sectionClass,
}: ParticulierParcoursNavProps) {
  return (
    <section
      className={cn(
        variant === 'rich'
          ? cn('border-t border-stone-100 bg-stone-50/40', PARTICULIER_SECTION.standard)
          : PARTICULIER_SECTION.standard,
        sectionClass,
      )}
    >
      <Container>
        <ParticulierSectionHeader
          eyebrow={eyebrow}
          title={title}
          description={description}
          align="center"
        />

        <Reveal delay={0.1}>
          <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {PARCOURS_ORDER.map((slug, idx) => {
              const p = PARCOURS_PARTICULIERS.find((x) => x.slug === slug);
              if (!p) return null;
              const Icon = getIcon(p.iconName);
              const isCurrent = slug === currentSlug;
              return (
                <a
                  key={slug}
                  href={`/particuliers/${slug}`}
                  className={cn(
                    'group relative flex flex-col gap-2 rounded-2xl border p-5 transition-all duration-200 ease-out',
                    isCurrent
                      ? 'border-brand-300 bg-gradient-to-br from-brand-50 via-paper to-paper shadow-[0_8px_24px_-8px_rgba(16,185,129,.25)] ring-1 ring-inset ring-brand-200/50'
                      : 'border-stone-200 bg-paper hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-[0_8px_24px_-8px_rgba(10,10,10,.08)]',
                  )}
                  aria-current={isCurrent ? 'page' : undefined}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                      Étape {idx + 1}
                    </span>
                    {isCurrent && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-brand-500 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-white">
                        <span className="h-1 w-1 rounded-full bg-white animate-glow-pulse" />
                        Vous êtes ici
                      </span>
                    )}
                  </div>
                  <span
                    className={cn(
                      'inline-flex h-9 w-9 items-center justify-center rounded-lg ring-1 ring-inset',
                      isCurrent
                        ? 'bg-brand-100 text-brand-800 ring-brand-300/60'
                        : 'bg-stone-50 text-stone-600 ring-stone-200/60 group-hover:bg-brand-50 group-hover:text-brand-700',
                    )}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.7} />
                  </span>
                  <p className="font-display text-sm font-bold text-ink">{p.title}</p>
                  <p className="line-clamp-2 text-[12px] leading-relaxed text-stone-600">
                    {p.eyebrow}
                  </p>
                  {!isCurrent && (
                    <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-brand-700 transition-transform duration-150 ease-out group-hover:translate-x-0.5">
                      Découvrir
                      <ArrowRight className="h-3 w-3" strokeWidth={2.4} />
                    </p>
                  )}
                </a>
              );
            })}
          </div>
        </Reveal>
      </Container>
    </section>
  );
}
